(() => {
  if (customElements.get('emery-cart-quantity')) return;

  let pending = false;
  let confirming = false;
  let confirmationId = 0;
  const controls = new Set();
  const updateControls = () => controls.forEach((control) => control.render());
  const maximumFor = (control) => {
    const value = control.dataset.max;
    return value !== undefined && value.trim() !== '' && Number.isFinite(Number(value)) ? Number(value) : Infinity;
  };

  function confirmRemoval(control, trigger) {
    const drawer = control.closest('cart-drawer');
    if (!drawer || !control.isConnected || confirming) return Promise.resolve(false);
    confirming = true;
    const id = `emery-cart-remove-${++confirmationId}`;
    const dialog = document.createElement('dialog');
    dialog.className = 'emery-cart-remove-dialog';
    dialog.setAttribute('aria-labelledby', `${id}-title`);
    dialog.setAttribute('aria-describedby', `${id}-description`);
    dialog.innerHTML = `
      <h2 class="h5" id="${id}-title">Remove item?</h2>
      <p id="${id}-description"></p>
      <div class="emery-cart-remove-dialog__actions">
        <button type="button" class="button button--secondary" data-keep-item autofocus>Keep item</button>
        <button type="button" class="button" data-remove-item>Remove item</button>
      </div>`;
    dialog.querySelector('p').textContent = `Remove “${control.dataset.title}” from your cart?`;
    const keepButton = dialog.querySelector('[data-keep-item]');
    const removeButton = dialog.querySelector('[data-remove-item]');
    const drawerTrap = drawer.focusTrap;

    return new Promise((resolve) => {
      let settled = false;
      const finish = (remove) => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        if (dialog.open) dialog.close();
        dialog.remove();
        confirming = false;
        if (drawer.open) drawerTrap?.unpause();
        if (!remove && trigger.isConnected && drawer.open) trigger.focus({ preventScroll: true });
        resolve(remove && control.isConnected);
      };
      const observer = new MutationObserver(() => {
        if (!control.isConnected || !dialog.isConnected || !drawer.open) finish(false);
      });
      keepButton.addEventListener('click', () => finish(false));
      removeButton.addEventListener('click', () => finish(true));
      dialog.addEventListener('cancel', (event) => { event.preventDefault(); finish(false); });
      dialog.addEventListener('close', () => finish(false));
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          finish(false);
        } else if (event.key === 'Tab') {
          event.preventDefault();
          (document.activeElement === keepButton ? removeButton : keepButton).focus();
        }
      });
      drawerTrap?.pause();
      drawer.append(dialog);
      observer.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
      try {
        dialog.showModal();
        keepButton.focus({ preventScroll: true });
      } catch {
        finish(false);
      }
    });
  }

  // Native cart rendering replaces each line after its update animation. Keep
  // controls locked until that replacement so repeated clicks cannot use stale
  // quantities or line keys, and restore keyboard focus to the new button.
  function waitForReplacement(control, direction) {
    const drawer = control.closest('cart-drawer');
    if (!drawer || !control.isConnected) return Promise.resolve();
    const lineKey = control.dataset.lineKey;
    return new Promise((resolve) => {
      let timer;
      const finish = () => {
        observer.disconnect();
        clearTimeout(timer);
        const matching = [...drawer.querySelectorAll('emery-cart-quantity')]
          .find((item) => item.dataset.lineKey === lineKey && item.getClientRects().length);
        const button = matching?.querySelector(`[data-quantity-step="${direction}"]`);
        resolve(button);
      };
      const observer = new MutationObserver(() => { if (!control.isConnected) finish(); });
      observer.observe(drawer, { childList: true, subtree: true });
      timer = setTimeout(finish, 2500);
    });
  }

  class EmeryCartQuantity extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      controls.add(this);
      this.addEventListener('click', (event) => this.change(event), { signal: this.abortController.signal });
      this.render();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      controls.delete(this);
    }

    render() {
      const quantity = Number(this.dataset.quantity);
      const minimum = Math.max(1, Number(this.dataset.min) || 1);
      const maximum = maximumFor(this);
      if (pending) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
      this.querySelector('[data-quantity-step="-1"]').disabled = pending || (minimum > 1 && quantity <= minimum);
      this.querySelector('[data-quantity-step="1"]').disabled = pending || quantity >= maximum;
      this.querySelector('.emery-cart-quantity__value').textContent = String(quantity);
      this.querySelector('input').value = String(quantity);
    }

    async change(event) {
      const button = event.target.closest('button[data-quantity-step]');
      if (!button || !this.contains(button) || button.disabled || pending || confirming) return;
      event.preventDefault();
      const direction = Number(button.dataset.quantityStep);
      const current = Number(this.dataset.quantity);
      const minimum = Math.max(1, Number(this.dataset.min) || 1);
      const increment = Math.max(1, Number(this.dataset.step) || 1);
      const maximum = maximumFor(this);
      const focused = document.activeElement === button;
      let target = direction > 0
        ? Math.min(maximum, current + increment)
        : Math.max(minimum, current - increment);
      if (direction < 0 && current === 1) {
        if (!await confirmRemoval(this, button)) return;
        target = 0;
      }
      if (!Number.isSafeInteger(target) || target === current) return;

      const nativeQuantity = this.closest('line-item-quantity');
      const lineItem = this.closest('line-item');
      if (typeof nativeQuantity?._changeLineItemQuantity !== 'function' || !lineItem) return;
      let succeeded = false;
      let replacementButton;
      const onChanged = (changeEvent) => {
        succeeded = true;
        this.dataset.quantity = String(changeEvent.detail.quantity);
        this.render();
      };
      pending = true;
      updateControls();
      lineItem.addEventListener('line-item:change', onChanged, { once: true });
      try {
        // Reuse the theme's native update, error, section-refresh and gift logic.
        await nativeQuantity._changeLineItemQuantity(this.dataset.lineKey, target);
        if (succeeded) replacementButton = await waitForReplacement(this, direction);
      } catch {
        lineItem.dispatchEvent(new CustomEvent('line-item:error', { bubbles: true }));
        let error = lineItem.querySelector('[data-emery-quantity-error]');
        if (!error) {
          error = document.createElement('p');
          error.dataset.emeryQuantityError = '';
          error.className = 'text-sm text-error';
          error.setAttribute('role', 'alert');
          lineItem.querySelector('.line-item__info').append(error);
        }
        error.textContent = 'Your cart could not be updated. Please try again.';
      } finally {
        lineItem.removeEventListener('line-item:change', onChanged);
        pending = false;
        updateControls();
        if (focused && replacementButton?.isConnected && !replacementButton.disabled) replacementButton.focus({ preventScroll: true });
      }
    }
  }

  customElements.whenDefined('line-item-quantity').then(() => {
    if (!customElements.get('emery-cart-quantity')) customElements.define('emery-cart-quantity', EmeryCartQuantity);
  });
})();
